import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { 
    LogOut, 
    ChevronLeft,
    School
} from 'lucide-react';

const YearSelection = () => {
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);
    const { selectYear, logout, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchYears = async () => {
            try {
                const response = await api.get('/academic-years?all=true');
                const yearsData = response.data.data || response.data;
                setYears(yearsData);
            } catch (error) {
                console.error("Error fetching academic years", error);
            } finally {
                setLoading(false);
            }
        };

        fetchYears();
    }, []);

    const handleSelect = (year) => {
        selectYear(year);
        navigate('/dashboard');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-slate-50 to-blue-50 text-slate-900 font-sans flex flex-col items-center justify-center p-6 relative overflow-hidden">

            {/* ☀️ Bright gradient blobs */}
            <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-[160px]"></div>
            <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-amber-200/30 rounded-full blur-[160px]"></div>
            <div className="absolute top-1/4 right-1/3 w-[400px] h-[300px] bg-pink-200/20 rounded-full blur-[140px]"></div>

            {/* ✨ Subtle grid */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb22_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb22_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none"></div>

            {/* 📦 Main Container */}
            <div className="w-full max-w-4xl relative z-10 bg-white/90 backdrop-blur-lg border border-slate-200/60 rounded-2xl p-6 ">

                {/* Header */}
                <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-4">
                    <div>
                        <h1 className="text-xl font-bold text-slate-800">
                            مرحباً بك، {user?.name}
                        </h1>
                        <p className="text-slate-500 text-sm">
                            الرجاء اختيار السنة الدراسية للمتابعة
                        </p>
                    </div>
                    
                    <button 
                        onClick={logout}
                        className="flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-red-500 transition-colors text-sm font-medium"
                    >
                        <LogOut size={18} />
                        <span>تسجيل الخروج</span>
                    </button>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {years.map((year) => (
                        <div
                            key={year.id}
                            onClick={() => handleSelect(year)}
                            className="group cursor-pointer bg-white border border-slate-200/60 rounded-xl px-4 py-3 hover:border-primary hover:shadow-sm transition-all flex items-center justify-between"
                        >
                            {/* LEFT */}
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                                    <School size={20} />
                                </div>

                                <h3 className="text-sm font-semibold text-slate-800 group-hover:text-primary transition-colors">
                                    {year.name}
                                </h3>
                            </div>

                            {/* RIGHT */}
                            <ChevronLeft
                                size={18}
                                className="text-primary group-hover:-translate-x-1 transition-transform"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default YearSelection;